import { makeAutoObservable } from 'mobx';
import ScenarioService from 'src/domain/service/ScenarioService';
import { CurrentStep } from 'src/stores/createScenarioStore';
import RootStore from 'src/stores/rootStore';
import Utils from 'src/utils/utils';

interface ScheduledRun {
  id: number;
  scheduleStartTime: string | null;
  name: string;
  description: string;
}

type LocalCurrentStep = 'FETCHING' | 'DONE' | 'REFETCHING' | 'SUBMITTING';

export default class ScheduledRunsStore {
  _: RootStore;

  currentStep: LocalCurrentStep = 'FETCHING';

  errorDescription = '';

  scheduledRuns: Array<ScheduledRun> = [];

  constructor(rootStore: RootStore, readonly scenarioService: ScenarioService) {
    makeAutoObservable(this, { _: false });
    this._ = rootStore;
  }

  async fetchScheduledRuns(): Promise<void> {
    this.setCurrentStep('FETCHING');
    await this.getScheduledRuns();
  }

  async refetchScheduledRuns(): Promise<void> {
    this.setCurrentStep('REFETCHING');
    await this.getScheduledRuns();
  }

  private async getScheduledRuns(): Promise<void> {
    try {
      const schedules = await this.scenarioService.getByScenarioState({
        states: 'SanityCheckSuccessful',
        hasScheduleTime: true,
      });
      const mappedScheduledRuns = schedules.map((run) => ({
        id: run.id,
        name: run.name,
        description: run.description,
        scheduleStartTime: run.scheduleStartTime,
      }));
      this.setScheduledRuns(mappedScheduledRuns);
    } catch (err) {
      this.errorDescription = Utils.getErrorMessage(err);
    } finally {
      this.setCurrentStep('DONE');
    }
  }

  async scheduleScenario(scenarioId: number, scheduleStartTime: string | null): Promise<void> {
    try {
      this.setCurrentStep('SUBMITTING');
      await this.scenarioService.scheduleRunScenario(scenarioId, scheduleStartTime);
      this.refreshSchedules(scenarioId, scheduleStartTime);
    } finally {
      this.setCurrentStep('DONE');
    }
  }

  public async refreshSchedules(
    scenarioId: number,
    scheduleStartTime: string | null,
  ): Promise<void> {
    const newScheduledRuns = this.scheduledRuns.filter((s) => s.id !== scenarioId);
    const scenarioToAdd = await this.scenarioService.get(scenarioId);
    if (scheduleStartTime !== null) {
      this.setScheduledRuns([
        ...newScheduledRuns,
        {
          id: scenarioToAdd.id,
          name: scenarioToAdd.name,
          description: scenarioToAdd.description,
          scheduleStartTime,
        },
      ]);
    } else {
      this.setScheduledRuns(newScheduledRuns);
    }
  }

  async runScenario(scenarioId: number): Promise<void> {
    try {
      this._.createScenarioStore.setCurrentStep(CurrentStep.RUN_MODEL);
      this.setCurrentStep('SUBMITTING');
      await this.scenarioService.runScenario(scenarioId);
      const newScheduledRuns = this.scheduledRuns.filter((s) => s.id !== scenarioId);
      this.setScheduledRuns(newScheduledRuns);
    } catch (err) {
      this._.createScenarioStore.setCurrentStep(CurrentStep.VALIDATION_MODEL_SUCCESS);
    } finally {
      this.setCurrentStep('DONE');
    }
  }

  async cancelScheduledRun(scenarioId: number): Promise<void> {
    await this.scheduleScenario(scenarioId, null);
  }

  public setScheduledRuns(scheduledRuns: Array<ScheduledRun>): void {
    this.scheduledRuns = scheduledRuns;
  }

  public setCurrentStep(currentStep: LocalCurrentStep): void {
    this.currentStep = currentStep;
  }

  get isRefetching(): boolean {
    return this.currentStep === 'REFETCHING';
  }

  get isLoading(): boolean {
    return this.currentStep === 'FETCHING';
  }

  get isSubmitting(): boolean {
    return this.currentStep === 'SUBMITTING';
  }
}
